import { SkinnyEmployee } from "./employee";

export enum FilterComparison {
	Equals = "eq",
	GreaterThan = "gt",
	GreaterThanOrEqual = "gte",
	LessThan = "lt",
	After = "after",
	Before = "before",
	LessThanOrEqual = "lte",
	NotEqual = "ne",
	Contains = "contains",
	NotContains = "notcontains",
	StartsWith = "startswith",
	NotStartsWith = "notstartswith",
	EndsWith = "endswith",
	NotEndsWith = "notendswith",
	ChainOfCommand = "chainofcommand",
	Null = "NULL",
}

export enum ColumnType {
	EMPLOYEE_FIELD = "employee",
	UNIQUE_QUESTION = "question",
	REVIEW_FORM_FIELD = "reviewform",
	REVIEW_FIELD = "review",
	REVIEW_CYCLE_FIELD = "reviewcycle",
	GROUPED_QUESTION = "groupedquestion",
	GOAL_FIELD = "goal",
	SURVEY_FIELD = "survey",
}

export type FieldValueObject = { value: FieldValue; label?: string; type?: string };
export type Value = FieldValue | FieldValueObject;
export type InputValue = Value | Value[] | null;

export type FieldValue = string | null | Date | boolean | (string | Date)[];

export type FieldCategory =
	| "EMPLOYEE_FIELD"
	| "GOAL_FIELD"
	| "REVIEW_FIELD"
	| "REVIEW_CYCLE_FIELD"
	| "REVIEW_FORM_FIELD"
	| "REVIEW_REPORT_FIELD"
	| "SUBJECT"
	| "SURVEY_FIELD";
export interface FieldFilter {
	value: FieldValue | FieldValue[];
	field: string;
	comparison: FilterComparison;
	type: string;
	category: FieldCategory;
}

// the quick filter for the report quick filter slide out panel
export interface SelectedFilter {
	type: string;
	value: any;
}

// can be string or Moment date type
export interface DateQuickFilterValue {
	start: string | Date;
	end: string | Date;
}
export interface ReviewStatusFilterValue {
	value: ReviewStatusFilters[];
}

export interface ReviewReportStatusFilterValue {
	value: ReviewReportStatusFilters[];
}
export interface ReviewCycleNames {
	value: ReviewCycleNameValue[];
	options: ReviewCycleNameValue[];
}

export interface ReviewFormNames {
	value: ReviewFormNameValue[];
	options: ReviewFormNameValue[];
}

export interface GroupNames {
	value: GroupNameValue[];
	options: GroupNameValue[];
}
export interface SubjectIdValues {
	value: SubjectIdValue[];
	options: SubjectIdValue[];
}

export interface ReviewCycleNameValue {
	value: string;
	label: string;
}
export interface ReviewFormNameValue {
	value: string;
	label: string;
}

export interface GroupNameValue {
	value: string;
	label: string;
}
export interface SubjectIdValue {
	value: string;
	label: string;
}

export interface EmployeeStatusValue {
	value: EmployeeStatusFilters[];
}

export enum GoalStatusFilters {
	Active = "active",
	Completed = "done",
	Archived = "archived",
}

export enum GoalTransparencyFilters {
	Private = "private",
	Public = "public",
}

export enum EmployeeStatusFilters {
	Active = "Active",
	Deactivated = "Deactivated",
}

export enum ReviewStatusFilters {
	Completed = "Completed",
	Active = "Active",
	Scheduled = "Scheduled",
}

export enum ReviewCycleStatusFilters {
	Completed = "completed",
	Active = "active",
	Scheduled = "scheduled",
}

export enum ReviewReportStatusFilters {
	Completed = "completed",
	Deleted = "deleted",
	InProgress = "in-progress",
	InProgressAwaitingSubmission = "in-progress-awaiting-authors",
	InProgressAwaitingSignOff = "in-progress-awaiting-signoff",
	NotStarted = "not-started",
}

export enum SurveyStatusFilters {
	Open = "Open",
	Scheduled = "Scheduled",
	Closed = "Closed",
}

export enum SurveyResponseStatusFilters {
	Submitted = "submitted",
	NotSubmitted = "not-submitted",
}

export interface ReportInfo {
	_id: string;
	name: string;
	type: "employeeReport" | "reviewReport" | "goalReport" | "surveyReport";
	description: string;
	tags: string[];
	createdAt: string;
	updatedAt: string;
	createdBy: {
		_id: string;
		name: string;
	};
	selectedColumns: string[];
}

export interface Report extends Omit<ReportInfo, "_id" | "createdAt" | "updatedAt"> {
	createdAt: Date;
	updatedAt: Date;
	questionGroupingSessionID?: string;
	sorts: GridSort[];
	quickFilter: SelectedFilter[];
	filters: FieldFilter[][] | null;
	columnMetadata: { [key: string]: IColumnMetadata };
	summarizeBy: string[];
	aggregationOperations: { [key: string]: SummaryFunction };
	isSummarizeRowsView: boolean;
	showAllAggregationColumns: boolean;
	summarizeByMetadata: { [key: string]: ISummarizeByMetadata };
	visualizeOptions: Partial<Record<string, any>>;
	pageSize: number;
	sharedWith?: string[];
	deletedAt?: Date;
}
export interface SavedQuery {
	_id?: string;
	company_id: string;
	type: "employeeReport" | "reviewReport" | "goalReport" | "surveyReport";
	name: string;
	description: string;
	filters: FieldFilter[][];
	employee_query_id?: string;
	question_grouping_session_id?: string;
	selected_data_fields: string[];
	tags: string[];
	sorts: GridSort[];
	selectedColumns: string[];
	columnMetadata: { [key: string]: IColumnMetadata };
	summarizeBy: string[];
	aggregationOperations: { [key: string]: SummaryFunction };
	isSummarizeRowsView: boolean;
	quickFilter: SelectedFilter[];
	createdBy: SkinnyEmployee;
	createdAt: string;
	updatedAt: string;
	showAllAggregationColumns: boolean;
	summarizeByMetadata?: { [key: string]: ISummarizeByMetadata };
	visualizeOptions: Partial<Record<string, any>>;
	pageSize: number;
	sharedWith?: string[];
	deletedAt?: Date;
}

// passed into the ag-grid columns, state of which columns are selected
export interface GridColumns {
	employeeFields: Column[];
	reviewFields: Column[];
	uniqueQuestions: Column[];
	groupedQuestions: Column[];
}

export interface Column {
	headerName: string;
	field: string;
	sortable: boolean;
	pinned?: string;
	cellRenderer?: string;
	cellClass: string;
	headerClass: string;
}

export interface GridColumn {
	headerName: string;
	field: string;
	sortable?: boolean;
	pinned?: "left" | "right" | null;
	cellRenderer?: string;
	fieldType:
		| "EMPLOYEE_FIELD"
		| "GOAL_FIELD"
		| "REVIEW_FIELD"
		| "REVIEW_FORM_FIELD"
		| "UNIQUE_QUESTION"
		| "GROUPED_QUESTION"
		| "REVIEW_CYCLE_FIELD"
		| "NON_UNIQUE_QUESTION"
		| "SURVEY_FIELD"
		| "SURVEY_QUESTION"
		| "SURVEY_QUESTION_GROUP";
	fieldValueType?: string;
	fieldValueTypeLabel?: string;
	cellClass?: string;
	headerClass?: string;
	fieldPath: string[];
	columnMetadata?: IColumnMetadata;
	summaryFunction?: string;
	supressMovable?: boolean;
	selection?: string | null;
	selectionOptions?: (string | number)[];
	defaultAggregationFunction?: SummaryFunction;
	valueGetter?: (params: any) => any;
}

export interface IColumnMetadata {
	width?: number;
	min?: number | null;
	max?: number | null;
}

// data set is used for the column picker slideout in ag-grid
export interface DataSet {
	id: string;
	label: string;
	values: DataValue[];
}

interface DataValue {
	id: string;
	label: string;
	sectionID: string;
	info: {
		[key: string]: string;
	};
}

export interface GridSort {
	name: string;
	direction: "asc" | "desc";
	fieldPath: string[];
	type: "EMPLOYEE_FIELD" | "REVIEW_FIELD" | "UNIQUE_QUESTION" | "GROUPED_QUESTION" | "GOAL_FIELD";
}

export interface SummarizeBy {
	type: "EMPLOYEE_FIELD" | "REVIEW_FIELD" | "REVIEW_FORM_FIELD" | "REVIEW_CYCLE_FIELD" | "GOAL_FIELD";
	name: string;
}

export interface AggregationOperation {
	type: "EMPLOYEE_FIELD" | "REVIEW_FIELD" | "UNIQUE_QUESTION" | "GROUPED_QUESTION" | "GOAL_FIELD";
	name: string;
	operation: SummaryFunction;
}

export interface IReportPaginationParams {
	pageNumber: number;
	pageSize?: number;
}

interface FormAwaitingApproval {
	id: string;
	review_parent: string;
	easy_add_data: {
		created_by: string;
		easy_add_config: string;
		requiresApproval: "admins-only" | "managers-and-admins" | null;
		allow_comments: boolean;
	};
	approval_status: "pending" | "declined";
}

interface Question {
	_id: string;
	answer: unknown;
	distinctCount?: number;
	question: string;
	type: string;
	hash: string;
	form_name: string;
	form_id: string;
	question_group: string | null;
}

export interface RawReviewReport {
	_id: string;
	review: {
		id: string;
		name: string;
		easy_add_enabled: boolean;
		forms_awaiting_approval: FormAwaitingApproval[];
	};
	questions: Question[];
	questions_map: {
		[key: string]: Question;
	};
	question_group_map: {
		[key: string]: Question[];
	};
	review_cycle: {
		id: string;
		name: string;
		group: string | null;
		is_360_enabled: boolean;
	};
	review_form: {
		id: string;
		name: string;
	};
	review_report: {
		id: string;
		authors: SkinnyEmployee[];
		signers: SkinnyEmployee[];
		authors_due_date: Date;
		signers_due_date: Date;
		completed_date: Date;
		status: string;
		authoring_completed_at: Date;
		signing_completed_at: Date;
	};
	subject: {
		[key: string]: unknown;
	};
}

export interface FilterOptions {
	[category: string]: {
		label: string;
		value: string;
	}[];
}

export enum SummaryFunction {
	Average = "average",
	Sum = "sum",
	Minimum = "minimum",
	Maximum = "maximum",
	Count = "count",
	Concatenate = "concatenate",
}

export interface ISummaryColumnMetadata {
	isColumnVisible: boolean;
	currentlySelectedSummaryFunction: SummaryFunction;
	availableSummaryFunctions: SummaryFunction[];
}

export interface ISummarizeByMetadata {
	selection: string;
}

export interface ChartOptions {
	showBellCurve?: boolean;
	binNum?: any;
	dataSeries?: {
		column: string;
		[key: string]: any; // any other options for the data series
	};
	xAxis?: {
		column: string;
		[key: string]: any;
	};
	yAxis?: {
		column: string;
		[key: string]: any;
	};
}

export interface IAvailableSummarizeField {
	value: string;
	label: string;
	group: string;
	type: string;
}
